import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Terminal, Search, Play, AlertTriangle, ChevronDown, ChevronRight,
  Clock, CheckCircle, XCircle, Zap, Loader2, Activity, Mail, Bell, ShieldCheck, Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const GROUP_META = {
  diagnostics: { label: 'Diagnostics', color: 'emerald', description: 'Read-only checks and audits' },
  maintenance: { label: 'Maintenance', color: 'amber', description: 'Routine cleanup and repair' },
  data_ops: { label: 'Data Ops', color: 'rose', description: 'Sponsor/study/participant lifecycle' },
  setup: { label: 'Setup & Seeding', color: 'indigo', description: 'Create roles, permissions, and test data' },
};

const QUICK_ACTION_COMMANDS = [
  { command: 'audit_system', icon: ShieldCheck },
  { command: 'check_reminder_system', icon: Bell },
  { command: 'check_mail_queue', icon: Mail },
  { command: 'check_password_expiration', icon: Key },
  { command: 'analyze_permissions', icon: Activity },
];

// Filter chips use the brand colour when selected (as on the Assessment Builder home).
const groupPillClass = (color, active) => (
  active
    ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
    : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-500'
);

// Group labels keep a soft tint per group so the kinds stay easy to tell apart.
const groupBadgeClass = (color) => {
  const map = {
    emerald: 'badge-success',
    amber: 'badge-warning',
    rose: 'badge-danger',
    indigo: 'badge-brand',
    gray: 'badge-gray',
  };
  return map[color] || map.gray;
};

const CommandRunner = () => {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [formState, setFormState] = useState({});
  const [runningId, setRunningId] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/api/dev/management-commands/');
        setCommands(res.data?.commands || []);
      } catch (e) {
        console.error('Failed to load management commands:', e);
        setError(e.response?.data?.error || e.message || 'Failed to load commands');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const commandsByName = useMemo(() => {
    const map = {};
    commands.forEach((c) => { map[c.command] = c; });
    return map;
  }, [commands]);

  const groupCounts = useMemo(() => {
    const counts = { all: commands.length };
    commands.forEach((c) => { counts[c.group] = (counts[c.group] || 0) + 1; });
    return counts;
  }, [commands]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commands.filter((c) => {
      if (activeGroup !== 'all' && c.group !== activeGroup) return false;
      if (!q) return true;
      return (
        c.command.toLowerCase().includes(q) ||
        c.label.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q)
      );
    });
  }, [commands, search, activeGroup]);

  const toggleExpand = (cmd) => {
    if (expandedId === cmd.command) {
      setExpandedId(null);
      return;
    }
    const defaults = {};
    cmd.positional.forEach((p) => { defaults[p.name] = ''; });
    Object.entries(cmd.args).forEach(([name, spec]) => {
      defaults[name] = spec.type === 'bool' ? false : '';
    });
    defaults.__confirm = '';
    setFormState(defaults);
    setExpandedId(cmd.command);
  };

  const updateField = (name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const runCommand = async (cmd, overrideArgs = null) => {
    const args = {};
    if (overrideArgs) {
      Object.assign(args, overrideArgs);
    } else {
      cmd.positional.forEach((p) => {
        const v = formState[p.name];
        if (v !== '' && v != null) args[p.name] = v;
      });
      Object.entries(cmd.args).forEach(([name, spec]) => {
        const v = formState[name];
        if (spec.type === 'bool') {
          if (v) args[name] = true;
        } else if (v !== '' && v != null) {
          args[name] = v;
        }
      });
    }

    // Validate required positionals
    const missingPositional = cmd.positional.find((p) => p.default === null && !(p.name in args));
    if (missingPositional) {
      toast.error(`"${missingPositional.name}" is required`);
      return;
    }
    // Validate required named args
    const missingNamed = Object.entries(cmd.args).find(([n, s]) => s.required && !(n in args));
    if (missingNamed) {
      toast.error(`"${missingNamed[0]}" is required`);
      return;
    }

    if (cmd.destructive && !overrideArgs && formState.__confirm !== 'RUN') {
      toast.error('Type RUN to confirm destructive command');
      return;
    }

    setRunningId(cmd.command);
    setLastResult(null);
    try {
      const payload = { command: cmd.command, args };
      if (cmd.destructive) payload.confirm = 'RUN';
      const res = await api.post('/api/dev/run-command/', payload);
      setLastResult({ ...res.data, ok: true });
      toast.success(`${cmd.label} completed in ${res.data.duration_ms}ms`);
    } catch (e) {
      const data = e.response?.data || {};
      setLastResult({
        ...data,
        ok: false,
        command: cmd.command,
        error: data.error || e.message,
      });
      toast.error(`${cmd.label} failed`);
    } finally {
      setRunningId(null);
    }
  };

  const renderArgInput = (cmd, name, spec) => {
    const value = formState[name];
    const common = 'input';
    if (spec.type === 'bool') {
      return (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => updateField(name, e.target.checked)}
            className="h-4 w-4"
          />
          <span>{spec.help || name}</span>
        </label>
      );
    }
    return (
      <input
        type={spec.type === 'int' ? 'number' : 'text'}
        value={value || ''}
        onChange={(e) => updateField(name, e.target.value)}
        placeholder={spec.help || name}
        className={common}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        Loading commands...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center rounded-lg border border-danger-500/25 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400">
        <XCircle className="mr-2 h-4 w-4 flex-shrink-0" />
        {error}
      </div>
    );
  }

  const quickActions = QUICK_ACTION_COMMANDS
    .map(({ command, icon }) => ({ icon, cmd: commandsByName[command] }))
    .filter((q) => q.cmd);

  return (
    <div className="space-y-6">
      {/* Quick actions */}
      {quickActions.length > 0 && (
        <div>
          <div className="mb-3 flex items-center">
            <Zap className="mr-1.5 h-3.5 w-3.5 text-warning-500" />
            <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Quick Actions</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {quickActions.map(({ cmd, icon: Icon }) => {
              const isRunning = runningId === cmd.command;
              return (
                <button
                  key={cmd.command}
                  disabled={isRunning || runningId !== null}
                  onClick={() => runCommand(cmd, {})}
                  className="flex flex-col items-start rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-400"
                >
                  <div className="mb-2 flex w-full items-center justify-between">
                    <Icon className="h-[18px] w-[18px] text-primary-600 dark:text-primary-400" />
                    {isRunning ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    ) : (
                      <Play className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{cmd.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{cmd.description}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search commands..."
            className="input pl-9 pr-3"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveGroup('all')}
            className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${groupPillClass('gray', activeGroup === 'all')}`}
          >
            All ({groupCounts.all || 0})
          </button>
          {Object.entries(GROUP_META).map(([id, meta]) => (
            <button
              key={id}
              onClick={() => setActiveGroup(id)}
              className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${groupPillClass(meta.color, activeGroup === id)}`}
            >
              {meta.label} ({groupCounts[id] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Command list */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No commands match your filters.
          </div>
        )}
        {filtered.map((cmd) => {
          const meta = GROUP_META[cmd.group] || GROUP_META.diagnostics;
          const isExpanded = expandedId === cmd.command;
          const isRunning = runningId === cmd.command;
          const hasArgs = cmd.positional.length > 0 || Object.keys(cmd.args).length > 0;
          return (
            <div
              key={cmd.command}
              className="card overflow-hidden"
            >
              <button
                onClick={() => toggleExpand(cmd)}
                className="flex w-full items-center p-4 text-left transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{cmd.label}</span>
                    <span className={`badge ${groupBadgeClass(meta.color)}`}>
                      {meta.label}
                    </span>
                    {cmd.destructive && (
                      <span className="badge badge-danger">
                        <AlertTriangle className="h-3 w-3" />
                        destructive
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{cmd.description}</div>
                  <code className="font-mono text-xs text-gray-400 dark:text-gray-500">{cmd.command}</code>
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="space-y-4 bg-gray-50/70 p-4 dark:bg-gray-900/40">
                      {hasArgs ? (
                        <>
                          {cmd.positional.length > 0 && (
                            <div>
                              <h5 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Positional</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {cmd.positional.map((p) => (
                                  <div key={p.name}>
                                    <label className="label">
                                      {p.name}
                                      {p.default === null && <span className="ml-0.5 text-danger-500">*</span>}
                                      {p.default && p.default !== '' && (
                                        <span className="ml-1 font-normal text-gray-400">(default: {String(p.default)})</span>
                                      )}
                                    </label>
                                    <input
                                      type="text"
                                      value={formState[p.name] || ''}
                                      onChange={(e) => updateField(p.name, e.target.value)}
                                      placeholder={p.default != null ? String(p.default) : p.name}
                                      className="input"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {Object.keys(cmd.args).length > 0 && (
                            <div>
                              <h5 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Options</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(cmd.args).map(([name, spec]) => (
                                  <div key={name}>
                                    {spec.type !== 'bool' && (
                                      <label className="label">
                                        --{name}
                                        {spec.required && <span className="ml-0.5 text-danger-500">*</span>}
                                      </label>
                                    )}
                                    {renderArgInput(cmd, name, spec)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No arguments.</p>
                      )}

                      {cmd.destructive && (
                        <div className="rounded-lg border border-danger-500/25 bg-danger-50 p-3 dark:bg-danger-500/10">
                          <label className="mb-1.5 flex items-center text-xs font-medium text-danger-700 dark:text-danger-400">
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            Type <code className="mx-1 rounded bg-danger-100 px-1 font-mono dark:bg-danger-500/20">RUN</code> to confirm
                          </label>
                          <input
                            type="text"
                            value={formState.__confirm || ''}
                            onChange={(e) => updateField('__confirm', e.target.value)}
                            placeholder="RUN"
                            className="input input-error font-mono"
                          />
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={() => runCommand(cmd)}
                          disabled={isRunning || runningId !== null}
                          className={`btn ${
                            cmd.destructive
                              ? 'btn-danger'
                              : 'btn-primary'
                          }`}
                        >
                          {isRunning ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                          Run
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Output panel */}
      <AnimatePresence>
        {lastResult && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700"
          >
            <div className={`flex items-center justify-between px-4 py-3 text-sm ${
              lastResult.ok
                ? 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400'
                : 'bg-danger-50 text-danger-700 dark:bg-danger-500/10 dark:text-danger-400'
            }`}>
              <div className="flex items-center">
                {lastResult.ok
                  ? <CheckCircle className="mr-2 h-4 w-4" />
                  : <XCircle className="mr-2 h-4 w-4" />}
                <span className="font-semibold">{lastResult.command}</span>
                {lastResult.ok ? (
                  <span className="ml-2 text-sm">completed</span>
                ) : (
                  <span className="ml-2 text-sm">failed</span>
                )}
              </div>
              {typeof lastResult.duration_ms === 'number' && (
                <div className="flex items-center text-sm">
                  <Clock className="mr-1 h-4 w-4" />
                  {lastResult.duration_ms}ms
                </div>
              )}
            </div>
            <div className="max-h-96 overflow-auto bg-gray-950 p-4 font-mono text-xs text-gray-100">
              {lastResult.error && (
                <div className="mb-2 text-danger-400">Error: {lastResult.error}</div>
              )}
              {lastResult.stdout && (
                <pre className="whitespace-pre-wrap">{lastResult.stdout}</pre>
              )}
              {lastResult.stderr && (
                <pre className="mt-2 whitespace-pre-wrap text-warning-400">{lastResult.stderr}</pre>
              )}
              {!lastResult.stdout && !lastResult.stderr && !lastResult.error && (
                <span className="text-gray-500">(no output)</span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommandRunner;
